// src/hooks/useTelemetryPrefs.js
//
// Per-user preferences for the Live Map telemetry panel: layout
// (floating bottom strip, docked-in-side-panel-below-People, or a side
// drawer on the map edge opposite the list), card
// visibility, card order, and collapsed state when docked.
//
// State lives at MODULE scope so all subscribers across the page see
// the same value — multiple call sites (LiveMapPage + TelemetryPanel
// + StaffTelemetryPanel) must stay in sync after any mutation.
// Per-instance useState would only re-render the calling component,
// leaving the other instance stale (the picker said "docked" but
// LiveMapPage's layout conditional still saw "floating", so the
// floating panel never unmounted and the layout flip looked broken).
// Matches the positionAnimator / snap-trails module patterns.

import { useCallback, useEffect, useReducer } from 'react';

const LS_LAYOUT      = 'trackizy_livemap_telemetry_layout';
const LS_VISIBILITY  = 'trackizy_livemap_telemetry_card_visibility';
const LS_ORDER       = 'trackizy_livemap_telemetry_card_order';
const LS_COLLAPSED   = 'trackizy_livemap_telemetry_collapsed';
const LS_DOCK_HEIGHT = 'trackizy_livemap_telemetry_dock_height_px';
const LS_SIDE_WIDTH  = 'trackizy_livemap_telemetry_side_width_px';

const LAYOUTS             = ['floating', 'docked', 'side'];
const DEFAULT_LAYOUT      = 'floating';
const DEFAULT_DOCK_HEIGHT = 280;
const MIN_DOCK_HEIGHT     = 120;
const MAX_DOCK_HEIGHT     = 700;

const DEFAULT_SIDE_WIDTH = 360; // matches the POI editor drawer on the same edge
const MIN_SIDE_WIDTH     = 280;
const MAX_SIDE_WIDTH     = 640;

function clampSideWidth(n) {
  if (!Number.isFinite(n)) return DEFAULT_SIDE_WIDTH;
  return Math.max(MIN_SIDE_WIDTH, Math.min(MAX_SIDE_WIDTH, Math.round(n)));
}

function readSideWidth() {
  try {
    const v = Number(localStorage.getItem(LS_SIDE_WIDTH));
    return Number.isFinite(v) && v > 0 ? clampSideWidth(v) : DEFAULT_SIDE_WIDTH;
  } catch {
    return DEFAULT_SIDE_WIDTH;
  }
}

function clampDockHeight(n) {
  if (!Number.isFinite(n)) return DEFAULT_DOCK_HEIGHT;
  return Math.max(MIN_DOCK_HEIGHT, Math.min(MAX_DOCK_HEIGHT, Math.round(n)));
}

function readLayout() {
  try {
    const v = localStorage.getItem(LS_LAYOUT);
    return LAYOUTS.includes(v) ? v : DEFAULT_LAYOUT;
  } catch {
    return DEFAULT_LAYOUT;
  }
}

function readVisibility() {
  try {
    const raw = localStorage.getItem(LS_VISIBILITY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function readOrder() {
  try {
    const raw = localStorage.getItem(LS_ORDER);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function readCollapsed() {
  try {
    return localStorage.getItem(LS_COLLAPSED) === 'true';
  } catch {
    return false;
  }
}

function readDockHeight() {
  try {
    const v = Number(localStorage.getItem(LS_DOCK_HEIGHT));
    return Number.isFinite(v) && v > 0 ? clampDockHeight(v) : DEFAULT_DOCK_HEIGHT;
  } catch {
    return DEFAULT_DOCK_HEIGHT;
  }
}

// Module-scope mutable state — shared by every hook instance.
const state = {
  layout: readLayout(),
  visibility: readVisibility(),
  order: readOrder(),
  collapsed: readCollapsed(),
  dockHeight: readDockHeight(),
  sideWidth: readSideWidth(),
};

const listeners = new Set();
function notify() {
  listeners.forEach((l) => l());
}

// Cross-tab updates — storage event fires on OTHER tabs only. Bind
// once at module load so every hook instance benefits without each
// installing its own listener.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === LS_LAYOUT)     { state.layout = readLayout(); notify(); }
    if (e.key === LS_VISIBILITY) { state.visibility = readVisibility(); notify(); }
    if (e.key === LS_ORDER)      { state.order = readOrder(); notify(); }
    if (e.key === LS_COLLAPSED)  { state.collapsed = readCollapsed(); notify(); }
    if (e.key === LS_DOCK_HEIGHT){ state.dockHeight = readDockHeight(); notify(); }
    if (e.key === LS_SIDE_WIDTH) { state.sideWidth = readSideWidth(); notify(); }
  });
}

/**
 * Test-only — resets module state between vitest test cases.
 * Production code should not call this. Tests should clear localStorage
 * first, then call this to re-read defaults into module state.
 */
export function __resetTelemetryPrefsForTests() {
  state.layout = readLayout();
  state.visibility = readVisibility();
  state.order = readOrder();
  state.collapsed = readCollapsed();
  state.dockHeight = readDockHeight();
  state.sideWidth = readSideWidth();
  notify();
}

// CAN detail cards that start hidden; an explicit `true` in the record shows them.
const DEFAULT_HIDDEN = new Set([
  'can_throttle', 'can_engineLoad', 'can_obdSpeed', 'can_fuelLevel', 'can_fuelUsed',
  'can_doorOpen', 'can_dtcCount', 'can_axleWeight1', 'can_axleWeight2', 'can_ignitionOnCount',
]);

export default function useTelemetryPrefs() {
  // forceUpdate via useReducer — every notify() bumps every subscriber.
  const [, forceUpdate] = useReducer((n) => n + 1, 0);
  useEffect(() => {
    const sub = () => forceUpdate();
    listeners.add(sub);
    return () => { listeners.delete(sub); };
  }, []);

  const setLayout = useCallback((next) => {
    const value = LAYOUTS.includes(next) ? next : DEFAULT_LAYOUT;
    state.layout = value;
    try { localStorage.setItem(LS_LAYOUT, value); } catch { /* private-mode safari etc. */ }
    notify();
  }, []);

  const setVisibility = useCallback((cardKey, visible) => {
    state.visibility = { ...state.visibility, [cardKey]: !!visible };
    try { localStorage.setItem(LS_VISIBILITY, JSON.stringify(state.visibility)); } catch { /* */ }
    notify();
  }, []);

  const setCardOrder = useCallback((order) => {
    state.order = Array.isArray(order) ? order.slice() : [];
    try { localStorage.setItem(LS_ORDER, JSON.stringify(state.order)); } catch { /* */ }
    notify();
  }, []);

  const setCollapsed = useCallback((value) => {
    state.collapsed = !!value;
    try { localStorage.setItem(LS_COLLAPSED, String(state.collapsed)); } catch { /* */ }
    notify();
  }, []);

  const setDockHeight = useCallback((px) => {
    state.dockHeight = clampDockHeight(Number(px));
    try { localStorage.setItem(LS_DOCK_HEIGHT, String(state.dockHeight)); } catch { /* */ }
    notify();
  }, []);

  const setSideWidth = useCallback((px) => {
    state.sideWidth = clampSideWidth(Number(px));
    try { localStorage.setItem(LS_SIDE_WIDTH, String(state.sideWidth)); } catch { /* */ }
    notify();
  }, []);

  // Convenience: empty visibility Record == "all on". Card keys never
  // explicitly set are visible by default — this is what makes new
  // cards forward-compatible.
  const isCardVisible = useCallback(
    (cardKey) => (state.visibility[cardKey] === undefined ? !DEFAULT_HIDDEN.has(cardKey) : state.visibility[cardKey] !== false),
    [],
  );

  const reset = useCallback(() => {
    // layout + collapsed are intentionally preserved — users keep their
    // dock-mode preference and current collapse state when they reset
    // card visibility / order.
    state.visibility = {};
    state.order = [];
    try {
      localStorage.removeItem(LS_VISIBILITY);
      localStorage.removeItem(LS_ORDER);
    } catch { /* */ }
    notify();
  }, []);

  return {
    layout: state.layout, setLayout,
    visibleCards: state.visibility, setVisibility, isCardVisible,
    cardOrder: state.order, setCardOrder,
    collapsed: state.collapsed, setCollapsed,
    dockHeight: state.dockHeight, setDockHeight,
    dockHeightBounds: { min: MIN_DOCK_HEIGHT, max: MAX_DOCK_HEIGHT, defaultValue: DEFAULT_DOCK_HEIGHT },
    sideWidth: state.sideWidth, setSideWidth,
    sideWidthBounds: { min: MIN_SIDE_WIDTH, max: MAX_SIDE_WIDTH, defaultValue: DEFAULT_SIDE_WIDTH },
    reset,
  };
}
